import React, { useState, useEffect } from 'react';
import CSOperationsDashboard from './CSOperationsDashboard';
import ShipmentDashboard from './ShipmentDashboard';
import PendingApprovalsPage from './PendingApprovalsPage';
import AgingReportSub from './AgingReportSub';
import BinOccupancyPage from './BinOccupancyPage';
import '../styles/operations-v6.css';

// ============================================================================
//  OperationsContainer — v6 shell with 5 sub-tabs  (v3: +Bin Occupancy)
//
//  This is the entry point for the "Operations Dashboard" master tab. It hosts:
//    1. 📊 Operations Dashboard
//    2. 🚢 Shipment Dashboard
//    3. ⏳ Pending Approvals
//    4. ⏱  Aging Report
//    5. 🗄 Bin Occupancy        ← NEW in v3
//
//  State owned at container level:
//    - page    : which sub-tab is active
//    - theme   : 'light' | 'dark' (toggles .dark class on the container)
//    - shift   : 'ALL' | 'DAY' | 'NIGHT' (passed down for sub-pages to use)
//
//  Tab state persists across reloads via localStorage so that refreshing
//  doesn't bounce the user back to the Operations Dashboard tab every time.
// ============================================================================

const LS_KEYS = {
  page:  'ops_v6_page',
  theme: 'ops_v6_theme',
  shift: 'ops_v6_shift',
};

export default function OperationsContainer() {
  const [page,  setPage]  = useState(() => localStorage.getItem(LS_KEYS.page)  || 'dashboard');
  const [theme, setTheme] = useState(() => localStorage.getItem(LS_KEYS.theme) || 'light');
  const [shift, setShift] = useState(() => localStorage.getItem(LS_KEYS.shift) || 'ALL');

  // Pending count badge — Chunk A fetches a lightweight count from reports.php.
  // Replaced with real data in Chunk B PendingApprovals rewrite.
  const [pendingCount, setPendingCount]  = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);

  useEffect(() => { localStorage.setItem(LS_KEYS.page,  page);  }, [page]);
  useEffect(() => { localStorage.setItem(LS_KEYS.theme, theme); }, [theme]);
  useEffect(() => { localStorage.setItem(LS_KEYS.shift, shift); }, [shift]);

  // Lightweight badge counts — single fetch on mount, refresh every 60s
  useEffect(() => {
    let cancelled = false;
    const loadBadges = async () => {
      try {
        const res = await fetch(
          (process.env.REACT_APP_API_URL || '/warehouse-system/api') +
          '/reports.php?action=ops_dashboard_v2',
          { credentials: 'include' }
        );
        const j = await res.json();
        if (cancelled) return;
        if (j?.success && j.data?.kpis) {
          setPendingCount(j.data.kpis.pendingApproval || 0);
        }
      } catch (_) { /* silent */ }
    };
    const loadAgingBadge = async () => {
      try {
        const res = await fetch(
          (process.env.REACT_APP_API_URL || '/warehouse-system/api') +
          '/reports.php?action=aging_report',
          { credentials: 'include' }
        );
        const j = await res.json();
        if (cancelled) return;
        if (j?.success && j.data?.kpis) {
          setCriticalCount(j.data.kpis.criticalCount || 0);
        }
      } catch (_) { /* silent */ }
    };
    loadBadges();
    loadAgingBadge();
    const t = setInterval(() => { loadBadges(); loadAgingBadge(); }, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const containerClass = 'ops-v6' + (theme === 'dark' ? ' dark' : '');

  return (
    <div className={containerClass}>
      {/* Local header inside the container — sits under the master nav */}
      <div className="ops-header">
        <div className="ops-header-title">
          <h1>📊 Cold Store Operations</h1>
          <div className="subtitle">Real-time situational view · Auto-refresh every 60s</div>
        </div>
        <div className="ops-header-controls">
          <div className="shift-filter">
            {['ALL', 'DAY', 'NIGHT'].map(s => (
              <button
                key={s}
                className={'shift-btn' + (shift === s ? ' active' : '')}
                onClick={() => setShift(s)}
              >
                {s}
              </button>
            ))}
          </div>
          <button
            className="ops-header-btn"
            onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
            title="Toggle theme"
          >
            {theme === 'light' ? '🌙 Dark' : '☀️ Light'}
          </button>
        </div>
      </div>

      {/* Sub-nav: 4 tabs */}
      <div className="pagenav">
        <div
          className={'pagenav-tab' + (page === 'dashboard' ? ' active' : '')}
          onClick={() => setPage('dashboard')}
        >
          📊 Operations Dashboard
        </div>
        <div
          className={'pagenav-tab' + (page === 'shipments' ? ' active' : '')}
          onClick={() => setPage('shipments')}
        >
          🚢 Shipment Dashboard
        </div>
        <div
          className={'pagenav-tab' + (page === 'pending' ? ' active' : '')}
          onClick={() => setPage('pending')}
        >
          ⏳ Pending Approvals
          {pendingCount > 0 && <span className="badge">{pendingCount}</span>}
        </div>
        <div
          className={'pagenav-tab' + (page === 'aging' ? ' active' : '')}
          onClick={() => setPage('aging')}
        >
          ⏱ Aging Report
          {criticalCount > 0 && <span className="badge">{criticalCount}</span>}
        </div>
        <div
          className={'pagenav-tab' + (page === 'bins' ? ' active' : '')}
          onClick={() => setPage('bins')}
        >
          🗄 Bin Occupancy
        </div>
      </div>

      {/* Active sub-page */}
      <div className="page">
        {page === 'dashboard' && <CSOperationsDashboard shift={shift} />}
        {page === 'shipments' && <ShipmentDashboard shift={shift} />}
        {page === 'pending'   && <PendingApprovalsPage shift={shift} />}
        {page === 'aging'     && <AgingReportSub shift={shift} />}
        {page === 'bins'      && <BinOccupancyPage shift={shift} />}
      </div>
    </div>
  );
}
